<template>
  <div
    :class="[
      'msgs-bubble-row', 
      isSentByMe && 'msgs-bubble-row--sent', 
      status === 'sending' && 'msgs-bubble-row--sending',
      status === 'failed' && 'msgs-bubble-row--failed'
    ]"
  >
    <template v-if="isTyping">
      <div class="msgs-bubble msgs-bubble--received msgs-bubble--typing">
        <div class="msgs-typing-dots">
          <span /><span /><span />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flex flex-col max-w-[70%] group">
        <div 
          :class="[
            'msgs-bubble', 
            isSentByMe ? 'msgs-bubble--sent' : isSystemConvo ? `msgs-bubble--${msg.severity || 'info'}` : 'msgs-bubble--received',
            status === 'sending' && 'opacity-60 pointer-events-none scale-[0.98]',
            status === 'failed' && 'border border-red-300 bg-red-50 dark:bg-red-950/20 text-red-900 dark:text-red-200',
            unfocused && 'opacity-75'
          ]"
        >
          <div class="msgs-bubble__severity" v-if="isSystemConvo && msg.severity">
            <font-awesome-icon :icon="severityIcon(msg.severity)" class="mr-1" />
            <span class="capitalize">{{ msg.severity }}</span>
          </div>
          
          <div v-if="msg.title" class="msgs-bubble__title">
            {{ msg.title }}
          </div>
          
          <div 
            class="msgs-bubble__text" 
            v-html="renderMarkdown(msg.content || msg.message)"
            @click="handleBubbleClick"
          />
          
          <div class="msgs-bubble__meta flex items-center justify-end gap-1">
            <span class="msgs-bubble__time">{{ formatTime(msg.created_at) }}</span>
            
            <template v-if="isSentByMe">
              <font-awesome-icon
                v-if="status === 'sending'"
                icon="fa-solid fa-circle-notch"
                class="fa-spin text-xs opacity-60"
              />
              <font-awesome-icon
                v-else-if="status === 'failed'"
                icon="fa-solid fa-circle-exclamation"
                class="text-red-500 text-xs"
              />
              <font-awesome-icon
                v-else-if="msg.read_at"
                icon="fa-solid fa-check-double"
                class="msgs-bubble__read"
                title="Read"
              />
              <font-awesome-icon
                v-else
                icon="fa-solid fa-check"
                class="msgs-bubble__sent"
                title="Sent"
              />
            </template>

            <font-awesome-icon
              v-if="isSystemConvo && isDismissed"
              icon="fa-solid fa-check-double"
              class="msgs-bubble__read"
              title="Dismissed"
            />
          </div>
        </div>

        <div v-if="status === 'failed'" class="text-xs text-red-500 mt-1 flex items-center gap-2 px-1 animate-fadeIn">
          <span>Failed to send</span>
          <button 
            @click="$emit('retry', msg)" 
            class="underline font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { marked } from 'marked'
import logoRawString from '@/assets/CruzControl_Logo_White_Large_Icon.svg?raw'
import { icon } from '@fortawesome/fontawesome-svg-core'

import { faLink } from '@awesome.me/kit-8acc95c3ba/icons/classic/solid'

export default {
  name: 'MessageChatBubble',
  props: {
    msg: {
      type: Object,
      default: () => ({})
    },
    isSentByMe: {
      type: Boolean,
      default: false
    },
    isSystemConvo: {
      type: Boolean,
      default: false
    },
    isDismissed: {
      type: Boolean,
      default: false
    },
    isTyping: {
      type: Boolean,
      default: false
    },
    status: {
      type: String,
      default: 'sent',
      validator: (val) => ['sent', 'sending', 'failed'].includes(val)
    },
    unfocused: {
      type: Boolean,
      default: false
    }
  },
  emits: ['retry'],
  methods: {
    formatTime(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    },
    severityIcon(severity) {
      switch (severity) {
        case 'warning': return 'fa-solid fa-triangle-exclamation'
        case 'danger': return 'fa-solid fa-circle-exclamation'
        case 'maintenance': return 'fa-solid fa-wrench'
        default: return 'fa-solid fa-circle-info'
      }
    },
    // ─── REUSABLE HTML "COMPONENT" FUNCTION ─────────────────────────
    createInternalLinkBadge(href, displayTitle) {
      // Programmatically grab the Font Awesome SVG string and inject Tailwind classes
      const iconSvgHtml = icon(faLink, { 
        classes: ['w-3.5', 'h-3.5', 'text-blue-500', 'inline-block'] 
      }).html[0]
      // 2. Wrap the raw SVG string in a span to force size and layout consistency via Tailwind.
      // The '[&_svg]:w-3.5' tells Tailwind to target the internal <svg> element directly.
      const iconHtml = `<span class="inline-flex items-center justify-center w-8 h-8 mr-2 [&_svg]:w-full [&_svg]:h-full [&_svg]:fill-current text-blue-500 group-hover/link:text-white dark:text-primary-100 dark:group-hover/link:text-primary-100">${logoRawString}</span>`

      // Return your single-line collapsed HTML string component
      return `<a href="${href}" class="msgs-bubble__internal-link-wrapper" title="${displayTitle}"><span class="mt-2 inline-flex items-center gap-1.5 bg-blue-50 dark:bg-primarydark/10 text-secondary-700 dark:text-primary-50 p-3 rounded-md border border-2 border-secondary-400 dark:border-primary-500 font-semibold text-sm transition-colors hover:bg-secondary hover:text-white hover:border-white dark:hover:bg-primarydark/20 dark:hover:border-primary-50 declaration-none select-none group/link">${iconHtml}${displayTitle}</span></a>`
    },
    // ─── MARKDOWN COMPILER ──────────────────────────────────────────
    renderMarkdown(text) {
      if (!text) return ''

      const renderer = new marked.Renderer()

      renderer.link = ({ href, text: linkText }) => {
        try {
          const isInternal = href.startsWith('/') || href.includes(window.location.host)

          if (isInternal) {
            const url = new URL(href, window.location.origin)
            let displayTitle = 'Internal Link'

            if (url.pathname.includes('active_work_orders')) {
              const id = url.searchParams.get('id')
              const shortId = id ? ` (#${id.slice(0, 5)})` : ''
              displayTitle = `View Work Order${shortId}`
            } else {
              const segments = url.pathname.split('/').filter(Boolean)
              // console.log("segments:", segments)
              if (segments.includes("asset-graph")) segments.pop()
              if (segments.length > 0) {
                displayTitle = segments[segments.length - 1]
                  .replace(/_/g, ' ')
                  .replace(/-/g, ' ')
                  .replace(/\b\w/g, c => c.toUpperCase())
              }
            }

            // ─── USE THE REUSABLE FUNCTION HERE ──────────────────────
            return this.createInternalLinkBadge(href, displayTitle)
          }
        } catch (e) {
          // Fallback to default rendering if standard parsing fails
        }

        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
      }

      return marked.parse(text, { renderer, gfm: true, breaks: true })
    },
    handleBubbleClick(event) {
    // 1. Find the closest anchor tag in the click's event path
    const link = event.target.closest('a')
    if (!link) return

    // 2. ─── MODIFIER KEY CHECK ───────────────────────────────────────
    // metaKey = Cmd on Mac / Windows key on Windows
    // ctrlKey = Ctrl key on Windows/Linux/Mac
    // shiftKey = Shift key (usually opens in a brand new window)
    // button === 1 = Middle-mouse scroll wheel click
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      // Return early! Let the browser execute its native new-tab behavior
      return
    }
    // ─────────────────────────────────────────────────────────────────

    // 3. Normal left-click interaction handling
    if (link.origin === window.location.origin) {
      // Stop the browser from triggering a full page reload
      event.preventDefault()

      // Feed it into Vue Router for an instantaneous SPA transition
      const localPath = link.pathname + link.search + link.hash
      this.$router.push(localPath).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('[Router Intercept] Navigation error:', err)
        }
      })
    }
  },
  }
}
</script>

<style scoped>
/* ── Chat bubbles ────────────────────────── */
.msgs-bubble-row {
  display: flex;
  justify-content: flex-start;
  padding: 0.125rem 0;
}
.msgs-bubble-row--sent {
  justify-content: flex-end;
}
.msgs-bubble {
  position: relative;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  text-align: left;
  border: 1px solid #f1f1f1;
}

[data-theme="dark"] .msgs-bubble {
  background: #1a1f35;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  border: 0;
}

/* Light mode */
.msgs-bubble--warning {
  background:
    radial-gradient(
      circle at top left,
      rgba(245, 158, 11, 0.14) 0%,
      rgba(245, 158, 11, 0.06) 30%,
      transparent 85%
    ),
    white;
}

.msgs-bubble--danger {
  background:
    radial-gradient(
      circle at top left,
      rgba(239, 68, 68, 0.14) 0%,
      rgba(239, 68, 68, 0.06) 30%,
      transparent 85%
    ),
    white;
}

.msgs-bubble--maintenance {
  background:
    radial-gradient(
      circle at top left,
      rgba(139, 92, 246, 0.14) 0%,
      rgba(139, 92, 246, 0.06) 30%,
      transparent 85%
    ),
    white;
}

.msgs-bubble--info {
  background:
    radial-gradient(
      circle at top left,
      rgba(59, 130, 246, 0.14) 0%,
      rgba(59, 130, 246, 0.06) 30%,
      transparent 85%
    ),
    white;
}

/* Dark mode */
[data-theme="dark"] .msgs-bubble--warning {
  background:
    radial-gradient(
      circle at top left,
      rgba(245, 158, 11, 0.18) 0%,
      rgba(245, 158, 11, 0.08) 30%,
      transparent 85%
    ),
    #1a1f35;
}

[data-theme="dark"] .msgs-bubble--danger {
  background:
    radial-gradient(
      circle at top left,
      rgba(239, 68, 68, 0.18) 0%,
      rgba(239, 68, 68, 0.08) 30%,
      transparent 85%
    ),
    #1a1f35;
}

[data-theme="dark"] .msgs-bubble--maintenance {
  background:
    radial-gradient(
      circle at top left,
      rgba(139, 92, 246, 0.18) 0%,
      rgba(139, 92, 246, 0.08) 30%,
      transparent 85%
    ),
    #1a1f35;
}

[data-theme="dark"] .msgs-bubble--info {
  background:
    radial-gradient(
      circle at top left,
      rgba(59, 130, 246, 0.18) 0%,
      rgba(59, 130, 246, 0.08) 30%,
      transparent 85%
    ),
    #1a1f35;
}
.msgs-bubble--received {
  background: white;
  border-radius: 0.75rem 0.75rem 0.75rem 0.125rem;
}
[data-theme="dark"] .msgs-bubble--received {
  background: rgb(20 26 40);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}
.msgs-bubble--sent {
  background: #098fb7;
  color: white;
  border-radius: 0.75rem 0.75rem 0.125rem 0.75rem;
}

.msgs-bubble__severity {
  display: flex;
  align-items: center;
  font-size: 0.6875rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.msgs-bubble--warning .msgs-bubble__severity { color: #d97706; }
.msgs-bubble--danger .msgs-bubble__severity { color: #ef4444; }
.msgs-bubble--maintenance .msgs-bubble__severity { color: #8b5cf6; }

.msgs-bubble__title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
}
[data-theme="dark"] .msgs-bubble__title { color: #f3f4f6; }

.msgs-bubble__text {
  font-size: 0.8125rem;
  color: #374151;
  line-height: 1.55;
  /* Crucial: Tells the browser it's allowed to split a word/URL at any character to prevent overflow */
  overflow-wrap: anywhere;
  word-break: break-word; /* Backwards compatibility for older engines */
  /* Optional but recommended: Keeps user line breaks and consecutive spaces intact */
  white-space: pre-wrap;
}
[data-theme="dark"] .msgs-bubble__text { color: #d1d5db; }
.msgs-bubble--sent .msgs-bubble__text { color: white; }
.msgs-bubble--sent .msgs-bubble__title { color: white; }

/* Markdown in bubbles */
.msgs-bubble__text :deep(p) { margin: 0 0 0.25rem; }
.msgs-bubble__text :deep(p:last-child) { margin-bottom: 0; }
.msgs-bubble__text :deep(a) { color: #93c5fd; text-decoration: underline; }
.msgs-bubble--received .msgs-bubble__text :deep(a) { color: #3b82f6; }
.msgs-bubble__text :deep(code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 0.1em 0.3em;
  border-radius: 0.2em;
  font-size: 0.85em;
}
.msgs-bubble--sent .msgs-bubble__text :deep(code) {
  background: rgba(255, 255, 255, 0.15);
}
[data-theme="dark"] .msgs-bubble--received .msgs-bubble__text :deep(code) {
  background: rgba(255, 255, 255, 0.08);
}
.msgs-bubble__text :deep(strong) { font-weight: 700; }
.msgs-bubble__text :deep(ul),
.msgs-bubble__text :deep(ol) {
  padding-left: 1rem;
  margin: 0.125rem 0;
}
/* Inside MessageChatBubble.vue <style scoped> */
.msgs-bubble__text :deep(.msgs-bubble__internal-link-wrapper) {
  text-decoration: none !important;
  display: inline-block;
  vertical-align: middle;
}

.msgs-bubble__text :deep(.msgs-bubble__internal-link-wrapper * ) {
  text-decoration: none !important;
}

.msgs-bubble__meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;
  margin-top: 0.375rem;
}
.msgs-bubble__time {
  font-size: 0.625rem;
  color: #9ca3af;
}
.msgs-bubble--sent .msgs-bubble__time { color: rgba(255, 255, 255, 0.65); }
.msgs-bubble__read { font-size: 0.625rem; color: #3b82f6; }
.msgs-bubble--sent .msgs-bubble__read { color: rgba(255, 255, 255, 0.8); }
.msgs-bubble__sent { font-size: 0.625rem; color: rgba(255, 255, 255, 0.5); }

/* ── Typing indicator ────────────────────── */
.msgs-bubble--typing {
  padding: 0.75rem 1rem;
}
.msgs-typing-dots {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}
.msgs-typing-dots span {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: #9ca3af;
  animation: typingBounce 1.2s infinite ease-in-out;
}
.msgs-typing-dots span:nth-child(2) { animation-delay: 0.15s; }
.msgs-typing-dots span:nth-child(3) { animation-delay: 0.3s; }
@keyframes typingBounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
}
</style>